import { useNavigate } from "react-router";
import type { Brand, SubscriptionStatus } from "@/modules/brand";
import type { ReactElement } from "react";

interface SubscriptionBannerProps {
	brand: Brand;
	workspaceId: string;
}

function formatDate(dateString: string): string {
	return new Date(dateString).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

interface BannerConfig {
	icon: ReactElement;
	bgColor: string;
	borderColor: string;
	textColor: string;
	buttonBgColor: string;
	getMessage?: (brand: Brand) => string;
	title?: string;
	description?: string;
	buttonText: string;
}

const bannerConfig: Record<Exclude<SubscriptionStatus, "subscription_active" | "subscription_lifetime">, BannerConfig> = {
	trial_active: {
		icon: (
			<svg className="h-4 w-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
				<path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
			</svg>
		),
		bgColor: "bg-amber-50",
		borderColor: "border-amber-200",
		textColor: "text-amber-800",
		buttonBgColor: "bg-amber-600 hover:bg-amber-700",
		getMessage: (brand) => (brand.trial_ends_at ? `Trial ends on ${formatDate(brand.trial_ends_at)}` : "You're on a free trial"),
		buttonText: "Subscribe Now",
	},
	trial_expired: {
		icon: (
			<svg className="h-4 w-4 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
				<path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
			</svg>
		),
		bgColor: "bg-red-50",
		borderColor: "border-red-200",
		textColor: "text-red-800",
		buttonBgColor: "bg-red-600 hover:bg-red-700",
		getMessage: (brand) => (brand.trial_ends_at ? `Trial expired on ${formatDate(brand.trial_ends_at)}` : "Your trial has expired"),
		buttonText: "Subscribe Now",
	},
	free_plan: {
		icon: (
			<svg className="h-4 w-4 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
				<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
			</svg>
		),
		bgColor: "bg-blue-50",
		borderColor: "border-blue-200",
		textColor: "text-blue-800",
		buttonBgColor: "bg-blue-600 hover:bg-blue-700",
		title: "Free plan active",
		description: "Unlock more features with Pro.",
		buttonText: "Upgrade",
	},
	subscription_cancelling: {
		icon: (
			<svg className="h-4 w-4 text-orange-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
				<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
			</svg>
		),
		bgColor: "bg-orange-50",
		borderColor: "border-orange-200",
		textColor: "text-orange-800",
		buttonBgColor: "bg-orange-600 hover:bg-orange-700",
		getMessage: (brand) => (brand.subscription_ends_at ? `Subscription ends on ${formatDate(brand.subscription_ends_at)}` : "Subscription ending soon"),
		buttonText: "Resubscribe",
	},
	subscription_cancelled: {
		icon: (
			<svg className="h-4 w-4 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
				<path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
			</svg>
		),
		bgColor: "bg-red-50",
		borderColor: "border-red-200",
		textColor: "text-red-800",
		buttonBgColor: "bg-red-600 hover:bg-red-700",
		getMessage: (brand) => (brand.subscription_ends_at ? `Subscription cancelled on ${formatDate(brand.subscription_ends_at)}` : "Subscription cancelled"),
		buttonText: "Subscribe Again",
	},
	subscription_past_due: {
		icon: (
			<svg className="h-4 w-4 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
				<path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
			</svg>
		),
		bgColor: "bg-red-50",
		borderColor: "border-red-200",
		textColor: "text-red-800",
		buttonBgColor: "bg-red-600 hover:bg-red-700",
		getMessage: () => "Payment failed. Please update your payment method.",
		buttonText: "Update Payment",
	},
	subscription_expired: {
		icon: (
			<svg className="h-4 w-4 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
				<path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
			</svg>
		),
		bgColor: "bg-red-50",
		borderColor: "border-red-200",
		textColor: "text-red-800",
		buttonBgColor: "bg-red-600 hover:bg-red-700",
		getMessage: () => "Subscription expired due to non-payment",
		buttonText: "Subscribe Now",
	},
};

// States that should show a banner
const bannerStates: SubscriptionStatus[] = [
	"trial_active",
	"trial_expired",
	"free_plan",
	"subscription_cancelling",
	"subscription_cancelled",
	"subscription_past_due",
	"subscription_expired",
];

export default function SubscriptionBanner({ brand, workspaceId }: SubscriptionBannerProps) {
	const navigate = useNavigate();

	// Only show banner to admin users
	if (brand.role !== "admin") {
		return null;
	}

	// Don't show banner for active, lifetime, or undefined status
	if (!brand.subscription_status || !bannerStates.includes(brand.subscription_status)) {
		return null;
	}

	const config = bannerConfig[brand.subscription_status as keyof typeof bannerConfig];

	if (!config) return null;

	const renderMessage = () => {
		if (config.title && config.description) {
			return (
				<div className="flex flex-col">
					<span className={`text-sm font-semibold ${config.textColor}`}>{config.title}</span>
					<span className={`text-xs font-light ${config.textColor}`}>{config.description}</span>
				</div>
			);
		}
		return <span className={`text-xs font-medium ${config.textColor}`}>{config.getMessage?.(brand)}</span>;
	};

	return (
		<div className={`rounded-lg ${config.bgColor} border ${config.borderColor} px-3 py-2.5`}>
			<div className="flex items-start gap-2">
				{config.icon}
				{renderMessage()}
			</div>
			<button
				onClick={() => navigate(`/${workspaceId}/billing`)}
				className={`mt-2 w-full rounded-md ${config.buttonBgColor} px-3 py-1.5 text-xs font-medium text-white transition-colors`}
			>
				{config.buttonText}
			</button>
		</div>
	);
}

// Preview component for development - shows all banner states
export function SubscriptionBannerPreview() {
	const baseBrand: Omit<Brand, "subscription_status"> = {
		id: "1",
		name: "Preview Brand",
		slug: "preview-brand",
		description: null,
		logo_url: null,
		website_url: "https://example.com",
		timezone: "UTC",
		status: "active",
		role: "admin",
		accepted_at: null,
		created_by_id: "1",
		created_at: new Date().toISOString(),
		updated_by_id: null,
		updated_at: null,
		deleted_by_id: null,
		deleted_at: null,
	};

	const mockBrands: Record<string, Brand> = {
		trial_active: {
			...baseBrand,
			subscription_status: "trial_active",
			trial_starts_at: new Date().toISOString(),
			trial_ends_at: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days from now
		},
		trial_expired: {
			...baseBrand,
			subscription_status: "trial_expired",
			trial_ends_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
		},
		free_plan: {
			...baseBrand,
			subscription_status: "free_plan",
		},
		subscription_active: {
			...baseBrand,
			subscription_status: "subscription_active",
			subscription_starts_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
			subscription_renews_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
		},
		subscription_cancelling: {
			...baseBrand,
			subscription_status: "subscription_cancelling",
			subscription_ends_at: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(), // 10 days from now
		},
		subscription_cancelled: {
			...baseBrand,
			subscription_status: "subscription_cancelled",
			subscription_ends_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
		},
		subscription_past_due: {
			...baseBrand,
			subscription_status: "subscription_past_due",
			subscription_renews_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
		},
		subscription_expired: {
			...baseBrand,
			subscription_status: "subscription_expired",
		},
		subscription_lifetime: {
			...baseBrand,
			subscription_status: "subscription_lifetime",
			subscription_starts_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
		},
	};

	return (
		<div className="space-y-6 max-w-xs">
			<div className="space-y-4">
				<div>
					<h3 className="text-sm font-medium text-gray-600 mb-2">1. Trial Active</h3>
					<SubscriptionBanner brand={mockBrands.trial_active} workspaceId="preview" />
				</div>

				<div>
					<h3 className="text-sm font-medium text-gray-600 mb-2">2. Trial Expired</h3>
					<SubscriptionBanner brand={mockBrands.trial_expired} workspaceId="preview" />
				</div>

				<div>
					<h3 className="text-sm font-medium text-gray-600 mb-2">3. Free Plan</h3>
					<SubscriptionBanner brand={mockBrands.free_plan} workspaceId="preview" />
				</div>

				<div>
					<h3 className="text-sm font-medium text-gray-600 mb-2">4. Subscription Active</h3>
					<div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2.5">
						<div className="flex items-center gap-2">
							<svg className="h-4 w-4 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
							</svg>
							<span className="text-xs font-medium text-green-800">No banner shown (active subscription)</span>
						</div>
					</div>
				</div>

				<div>
					<h3 className="text-sm font-medium text-gray-600 mb-2">5. Subscription Cancelling</h3>
					<SubscriptionBanner brand={mockBrands.subscription_cancelling} workspaceId="preview" />
				</div>

				<div>
					<h3 className="text-sm font-medium text-gray-600 mb-2">6. Subscription Cancelled</h3>
					<SubscriptionBanner brand={mockBrands.subscription_cancelled} workspaceId="preview" />
				</div>

				<div>
					<h3 className="text-sm font-medium text-gray-600 mb-2">7. Subscription Past Due</h3>
					<SubscriptionBanner brand={mockBrands.subscription_past_due} workspaceId="preview" />
				</div>

				<div>
					<h3 className="text-sm font-medium text-gray-600 mb-2">8. Subscription Expired</h3>
					<SubscriptionBanner brand={mockBrands.subscription_expired} workspaceId="preview" />
				</div>

				<div>
					<h3 className="text-sm font-medium text-gray-600 mb-2">9. Subscription Lifetime</h3>
					<div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2.5">
						<div className="flex items-center gap-2">
							<svg className="h-4 w-4 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
							</svg>
							<span className="text-xs font-medium text-green-800">No banner shown (lifetime deal)</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
